import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuSeparator, DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Building2, LayoutDashboard, Users, DollarSign, FolderKanban,
  Sparkles, Settings, LogOut, Menu, X, ChevronDown,
  TrendingUp, FileText, Receipt, Target, Briefcase, AlertTriangle,
  BookOpen, Package, Calendar, Bell, UserCircle, MessageCircle, Share2
} from 'lucide-react';

const navigation = [
  { 
    name: 'Dashboard', 
    href: '/dashboard', 
    icon: LayoutDashboard,
    description: 'Executive overview'
  },
  { 
    name: 'Clients', 
    href: '/clients', 
    icon: Building2,
    description: 'Client management'
  },
  { 
    name: 'Services', 
    href: '/services', 
    icon: Package,
    description: 'Service offerings'
  },
  { 
    name: 'Sales & CRM', 
    href: '/sales', 
    icon: TrendingUp,
    description: 'Leads, opportunities & quotations',
    children: [
      { name: 'Leads', href: '/sales?tab=leads', icon: Target },
      { name: 'Opportunities', href: '/sales?tab=opportunities', icon: Briefcase },
      { name: 'Quotations', href: '/sales?tab=quotations', icon: FileText }
    ]
  },
  { 
    name: 'Finance', 
    href: '/finance', 
    icon: DollarSign,
    description: 'Invoices & payments',
    children: [
      { name: 'Invoices', href: '/finance?tab=invoices', icon: Receipt },
      { name: 'Payments', href: '/finance?tab=payments', icon: DollarSign },
      { name: 'Accounting', href: '/accounting', icon: BookOpen },
      { name: 'Purchase Orders', href: '/purchase-orders', icon: Package }
    ]
  },
  { 
    name: 'Projects', 
    href: '/projects', 
    icon: FolderKanban,
    description: 'Projects, grants & risks',
    children: [
      { name: 'Projects', href: '/projects?tab=projects', icon: FolderKanban },
      { name: 'Tasks', href: '/projects?tab=tasks', icon: Target },
      { name: 'Grants', href: '/projects?tab=grants', icon: Receipt },
      { name: 'Risks', href: '/projects?tab=risks', icon: AlertTriangle }
    ]
  },
  { 
    name: 'Calendar', 
    href: '/calendar', 
    icon: Calendar,
    description: 'Schedule & reminders'
  },
  { 
    name: 'Social & Messaging', 
    href: '/social-config', 
    icon: Share2,
    description: 'Social integrations'
  },
  { 
    name: 'AI Assistant', 
    href: '/ai', 
    icon: Sparkles,
    description: 'AI-powered operations'
  },
  { 
    name: 'Settings', 
    href: '/settings', 
    icon: Settings,
    description: 'System configuration'
  }
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState([]);

  const toggleExpand = (name) => {
    setExpandedItems(prev => 
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => {
    const basePath = href.split('?')[0];
    return location.pathname === basePath;
  };

  const NavItem = ({ item, mobile = false }) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    const expanded = expandedItems.includes(item.name);

    if (item.children) {
      return (
        <div className="space-y-1">
          <button
            onClick={() => toggleExpand(item.name)}
            className={`w-full sidebar-item justify-between ${active ? 'sidebar-item-active' : ''}`}
          >
            <div className="flex items-center gap-3">
              <Icon className="h-5 w-5" />
              <span>{item.name}</span>
            </div>
            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
          {expanded && (
            <div className="ml-4 pl-4 border-l space-y-1">
              {item.children.map((child) => {
                const ChildIcon = child.icon;
                return (
                  <Link
                    key={child.name}
                    to={child.href}
                    onClick={() => mobile && setSidebarOpen(false)}
                    className="sidebar-item text-sm"
                  >
                    <ChildIcon className="h-4 w-4" />
                    <span>{child.name}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        to={item.href}
        onClick={() => mobile && setSidebarOpen(false)}
        className={`sidebar-item ${active ? 'sidebar-item-active' : ''}`}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className="h-5 w-5" />
        <span>{item.name}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r transform transition-transform duration-200 ease-in-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-slate-900 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-bold">IEMS</span>
            </Link>
            <button 
              className="lg:hidden p-2 rounded-md hover:bg-muted"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navigation.map((item) => (
                <NavItem key={item.name} item={item} mobile />
              ))}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-slate-900 text-white">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ') || 'User'}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem>
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-rose-600" data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-xl border-b flex items-center px-4 lg:px-8">
          <button
            className="lg:hidden p-2 rounded-md hover:bg-muted mr-4"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">
              {user?.branch === 'main' ? 'Main Branch' : user?.branch}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
